<script setup lang="ts">
import CoreIcon from '@core/core-icon.vue';
import CanvasMeshBackground, {type MeshState} from '@/components/canvas/canvas-mesh-background.vue';
import {computed, ref, type PropType} from 'vue';

const GRADIENT_CONFIGS: Record<MeshState, {
	angle: string;
	c1: string; c2: string; c3: string; c4: string; c5: string;
	shadow: string;
}> = {
	active: {
		angle: '203.74deg',
		c1: '#FA9DF1', c2: '#E06EFE', c3: '#B45DFF', c4: '#7E2EFF', c5: '#6125ED',
		shadow: '0px 0px 144px rgba(167, 81, 255, 0.25), inset 0px 0px 24px rgba(255, 255, 255, 0.5)',
	},
	expired: {
		angle: '130deg',
		c1: '#FF2C58', c2: '#EE6F57', c3: '#EE8F67', c4: '#EE8F67', c5: '#EE8F67',
		shadow: 'inset 0px 0px 24px rgba(255, 255, 255, 0.5)',
	},
	loading: {
		angle: '203.74deg',
		c1: '#A78BFA', c2: '#8B5CF6', c3: '#7C3AED', c4: '#6D28D9', c5: '#5B21B6',
		shadow: '0px 0px 144px rgba(115, 93, 207, 0.3), inset 0px 0px 24px rgba(255, 255, 255, 0.4)',
	},
};

const props = defineProps({
	icon: {
		type: Object,
		default: null
	},
	red: {
		type: Boolean,
		default: false
	},
	meshState: {
		type: String as PropType<MeshState>,
		default: 'active',
	},
});

const meshRef = ref<InstanceType<typeof CanvasMeshBackground> | null>(null);

const onHover = () => {
	meshRef.value?.pulse();
};

const onLeave = () => {
	meshRef.value?.unpulse();
};

const gradientStyle = computed(() => {
	const g = GRADIENT_CONFIGS[props.meshState] || GRADIENT_CONFIGS.active;
	return {
		'--bp-c1': g.c1,
		'--bp-c2': g.c2,
		'--bp-c3': g.c3,
		'--bp-c4': g.c4,
		'--bp-c5': g.c5,
		'--bp-shadow': g.shadow,
	};
});
</script>
<template>
	<button class="button-power" :style="gradientStyle" @mouseenter="onHover" @mouseleave="onLeave">
		<canvas-mesh-background ref="meshRef" :state="meshState" />
		<span class="button-power__inner">
			<span class="button-power__content font-16-pro-regular">
				<core-icon
					v-if="icon"
					:name="icon?.name"
					:viewBox="icon?.viewBox"
					class="button-power__icon"
				>
				</core-icon>
				<slot />
			</span>
		</span>
	</button>
</template>
<style lang="scss">
@property --bp-c1 { syntax: '<color>'; inherits: true; initial-value: #FA9DF1; }
@property --bp-c2 { syntax: '<color>'; inherits: true; initial-value: #E06EFE; }
@property --bp-c3 { syntax: '<color>'; inherits: true; initial-value: #B45DFF; }
@property --bp-c4 { syntax: '<color>'; inherits: true; initial-value: #7E2EFF; }
@property --bp-c5 { syntax: '<color>'; inherits: true; initial-value: #6125ED; }

.button-power {
	width: 23.2rem;
	height: 23.2rem;
	overflow: hidden;
	border-radius: 50%;
	display: block;
	position: relative;
	fill: currentColor;
	color: #fff;
	--bp-c1: #FA9DF1;
	--bp-c2: #E06EFE;
	--bp-c3: #B45DFF;
	--bp-c4: #7E2EFF;
	--bp-c5: #6125ED;
	background: linear-gradient(203.74deg, var(--bp-c1) -1.83%, var(--bp-c2) 26.73%, var(--bp-c3) 50.76%, var(--bp-c4) 79.87%, var(--bp-c5) 97.38%);
	box-shadow: var(--bp-shadow, 0px 0px 144px rgba(167, 81, 255, 0.25), inset 0px 0px 24px rgba(255, 255, 255, 0.5));
	transition: --bp-c1 0.8s ease, --bp-c2 0.8s ease, --bp-c3 0.8s ease, --bp-c4 0.8s ease, --bp-c5 0.8s ease;

	@include min-desktop {
		width: 27.2rem;
		height: 27.2rem;

		&:hover {
			.button-power {
				&__inner {
					transform: scale(1.02);
				}
			}
		}
	}

	.canvas-mesh-background {
		transition: transform 1s;
	}


	&__inner {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		z-index: 1;
		transition: transform 0.4s;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 1.6rem;
		text-align: center;
		align-items: center;
		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__icon {
		width: 3.6rem;

	}
}
</style>
